import React, { useMemo, useState, useEffect, useRef } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import { useTranslation } from "react-i18next";
import { getOrgTokens } from "../../../theme/style/organizationPalette";
import AgGrid from "../../../components/grid/AgGrid";
import {
  defaultColDef,
  leftAlignColDef,
  rightAlignColDef
} from "../../../components/grid/columnDefinitions/columnDefinitions";
import { useContextProvider } from "../../../components/page-context/ContextProvider";
import { Chip, DataTableCard, M3Button, M3GridSelectEditor, M3Surface } from "../../../components/m3";
import {
  CT_OPTIONS,
  CA_OPTIONS,
  INDEP_OPTIONS,
  GPE_OPTIONS,
  DEFAULT_DISASSEMBLY_FACTORS,
  getDefaultDisassemblyFactors
} from "./pciOptions";
import { isCircularityRowEditable } from "../../../functions/circularityLock";
import BuildingProductDropdown from "../../Building/components/BuildingProductDropdown";

// Dropdown options for the M3 grid editor: value as string, label shows the score and meaning.
const toEditorOptions = (options) => options.map((opt) => ({ value: String(opt.value), label: `${opt.value.toFixed(1)} - ${opt.label}` }));

const FACTOR_REFERENCES = [
  { title: "Connection Type (CT)", options: CT_OPTIONS, tone: "primary" },
  { title: "Connection Accessibility (CA)", options: CA_OPTIONS, tone: "info" },
  { title: "Independency (ID)", options: INDEP_OPTIONS, tone: "warning" },
  { title: "Geometry of Product Edge (GPE)", options: GPE_OPTIONS, tone: "tertiary" },
];

// Robust helper to parse number from any string representation or database fallback
const parseNumeric = (val, defaultVal = DEFAULT_DISASSEMBLY_FACTORS.ct) => {
  if (val === null || val === undefined) return defaultVal;
  if (typeof val === "number" && !isNaN(val)) return val;
  
  const str = String(val).trim();
  if (!str) return defaultVal;

  // Split by dash to check for formats like "1.0 - Dry connection" or "Dry connection - 1.0"
  if (str.includes(" - ")) {
    const parts = str.split(" - ");
    const lastNum = parseFloat(parts[parts.length - 1]);
    if (!isNaN(lastNum)) return lastNum;
    
    const firstNum = parseFloat(parts[0]);
    if (!isNaN(firstNum)) return firstNum;
  }

  const parsed = parseFloat(str);
  return isNaN(parsed) ? defaultVal : parsed;
};

const EXCLUDED_TYPES = [
  "Building Service Engineering",
  "Processes at Building Site",
  "End of Life",
  "Energy sources",
  "Transport",
  "Chemicals",
  "Refrigerants",
  "Water"
];

// `selectedSubcategoryId`/`onSubcategoryChange` are Building Circularity-only (never
// passed by Product/Project). When present, they render BuildingProductDropdown before
// the "Reset factors" button as a *controlled* component — the selection, the processes
// list, and the WBLCA fetch all live in BuildingCircularity.js (shared with the other
// Building Circularity tabs). `combined_data` here is already scoped to the selected
// product by the parent, so this component needs no Building-specific fetching of its
// own — the Material/Mass/MCI columns and every formula below behave exactly as they
// always did for Product/Project, just fed different rows.
const DesignAssessmentPanel = ({ combined_data, circularities, setCircularities, type, id, selectedSubcategoryId = null, onSubcategoryChange = null }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);
  const { setIsSaveVisible } = useContextProvider();

  const materials = useMemo(() => {
    const raw = combined_data?.combined_circularities || [];
    // Building Circularity shows every WBLCA material regardless of type — real building
    // materials in categories like Energy sources, Water, Chemicals, etc. often carry no
    // physical mass by nature (which is why they show blank/NA mass in the Environmental
    // Factor table), but they still need to appear here. Product/Project keep the
    // original exclusion (onSubcategoryChange is Building-only, never passed by them).
    if (onSubcategoryChange) return raw;
    const excludedSet = new Set(EXCLUDED_TYPES.map(t => t.toLowerCase().trim()));
    return raw.filter(m => {
      const typeVal = String(m.type || "").toLowerCase().trim();
      return !excludedSet.has(typeVal);
    });
  }, [combined_data, onSubcategoryChange]);

  // Load user factor configurations from localStorage
  const localStorageKey = `arivu_pci_selections_${type || "Product"}_${id || "default"}`;
  const [rowConfigs, setRowConfigs] = useState({});

  useEffect(() => {
    try {
      const saved = localStorage.getItem(localStorageKey);
      if (saved) {
        setRowConfigs(JSON.parse(saved));
      } else {
        setRowConfigs({});
      }
    } catch (e) {
      console.error("Failed to load PCI configurations from localStorage", e);
    }
  }, [localStorageKey]);

  // Handle configuration changes for a row
  const handleConfigChange = (materialId, key, value) => {
    const matchedCirc = circularities?.find(c => String(c.env_id) === String(materialId));
    const rowDefaults = getDefaultDisassemblyFactors(matchedCirc);
    const numericVal = parseNumeric(value, rowDefaults[key]);

    // Update local configs; an untouched row starts from its saved (or category default) factors
    const updated = {
      ...rowConfigs,
      [materialId]: {
        ...(rowConfigs[materialId] || {
          ct: parseNumeric(matchedCirc?.ct_factor, rowDefaults.ct),
          ca: parseNumeric(matchedCirc?.ca_factor, rowDefaults.ca),
          indep: parseNumeric(matchedCirc?.indep_factor, rowDefaults.indep),
          gpe: parseNumeric(matchedCirc?.gpe_factor, rowDefaults.gpe)
        }),
        [key]: numericVal
      }
    };
    setRowConfigs(updated);
    localStorage.setItem(localStorageKey, JSON.stringify(updated));

    // Make the header "Save" button visible instantly
    if (setIsSaveVisible) {
      setIsSaveVisible(true);
    }

    // Propagate changes to parent circularities state so that MCI and metrics recalculate
    if (setCircularities && circularities && circularities.length > 0) {
      const updatedCircs = circularities.map(c => {
        if (String(c.env_id) === String(materialId)) {
          return {
            ...c,
            ct_factor: key === "ct" ? numericVal : parseNumeric(c.ct_factor, rowDefaults.ct),
            ca_factor: key === "ca" ? numericVal : parseNumeric(c.ca_factor, rowDefaults.ca),
            indep_factor: key === "indep" ? numericVal : parseNumeric(c.indep_factor, rowDefaults.indep),
            gpe_factor: key === "gpe" ? numericVal : parseNumeric(c.gpe_factor, rowDefaults.gpe)
          };
        }
        return c;
      });
      setCircularities(updatedCircs);
    }
  };

  // Called when any cell value is modified in the AgGrid
  const onCellValueChanged = (params) => {
    if (!params.data || params.node.rowPinned === "bottom") return;
    const { id: materialId } = params.data;
    const { field } = params.colDef;
    handleConfigChange(materialId, field, params.newValue);
  };

  // Reset all row configurations to defaults
  const handleResetAll = () => {
    if (window.confirm(t("Are you sure you want to reset all connection factors to default values?"))) {
      setRowConfigs({});
      localStorage.removeItem(localStorageKey);

      if (setIsSaveVisible) {
        setIsSaveVisible(true);
      }

      // Revert parent state to each material's category default
      if (setCircularities && circularities && circularities.length > 0) {
        const resetCircs = circularities.map(c => {
          const defaults = getDefaultDisassemblyFactors(c);
          return {
            ...c,
            ct_factor: defaults.ct,
            ca_factor: defaults.ca,
            indep_factor: defaults.indep,
            gpe_factor: defaults.gpe
          };
        });
        setCircularities(resetCircs);
      }
    }
  };

  // Compute values for each row, safeguarding against NaN
  const computedRows = useMemo(() => {
    return materials.map((m) => {
      const matchedCirc = circularities?.find(c => String(c.env_id) === String(m.id));

      const rowDefaults = getDefaultDisassemblyFactors(matchedCirc);
      const config = rowConfigs[m.id] || {
        ct: parseNumeric(matchedCirc?.ct_factor, rowDefaults.ct),
        ca: parseNumeric(matchedCirc?.ca_factor, rowDefaults.ca),
        indep: parseNumeric(matchedCirc?.indep_factor, rowDefaults.indep),
        gpe: parseNumeric(matchedCirc?.gpe_factor, rowDefaults.gpe)
      };

      const mciVal = m.percentage_mci !== undefined
        ? Number(m.percentage_mci)
        : (matchedCirc?.percentage_mci !== undefined
          ? Number(matchedCirc.percentage_mci)
          : (m.MCI !== undefined
            ? Math.max(0, (Number(m.MCI) - 0.1) / 0.9)
            : (matchedCirc?.MCI !== undefined
              ? Math.max(0, (Number(matchedCirc.MCI) - 0.1) / 0.9)
              : 0.0)));
      const ct = parseNumeric(config.ct, rowDefaults.ct);
      const ca = parseNumeric(config.ca, rowDefaults.ca);
      const indep = parseNumeric(config.indep, rowDefaults.indep);
      const gpe = parseNumeric(config.gpe, rowDefaults.gpe);
      const fi = 4 / ((1 / ct) + (1 / ca) + (1 / indep) + (1 / gpe));
      const pci = mciVal * fi;
      const mass = m.mass || 0;

      return {
        ...m,
        ct,
        ca,
        indep,
        gpe,
        mciVal,
        fi,
        pci,
        mass
      };
    });
  }, [materials, rowConfigs, circularities]);

  // Overall metrics calculation
  const metrics = useMemo(() => {
    let totalMass = 0;
    let sumPciTimesMass = 0;
    let sumFiTimesMass = 0;
    let sumMciTimesFi = 0;

    computedRows.forEach((row) => {
      totalMass += row.mass;
      sumPciTimesMass += row.pci * row.mass;
      sumFiTimesMass += row.fi * row.mass;
      sumMciTimesFi += row.mciVal * row.fi;
    });

    const overallPciConnectionWeighted = computedRows.length > 0
      ? computedRows.reduce((sum, row) => sum + row.pci, 0) / computedRows.length
      : 0;

    const overallPciMassWeighted = totalMass > 0 ? sumPciTimesMass / totalMass : 0;
    const overallDisassemblyPotential = totalMass > 0 ? sumFiTimesMass / totalMass : 0;
    const totalFi = computedRows.reduce((sum, row) => sum + row.fi, 0);
    const circularityIndicator = totalFi > 0 ? sumMciTimesFi * (1 / totalFi) : 0;

    return {
      totalMass,
      overallPciMassWeighted,
      overallPciConnectionWeighted,
      overallDisassemblyPotential,
      circularityIndicator
    };
  }, [computedRows]);

  const gridCellFontSize = tok.font.size.gridCell || "15px";

  // Grid columns definition using standard AgGrid select cell editors with numeric options
  const colDefs = useMemo(() => {
    const numericFormatter = (params) => {
      return params.value !== null && params.value !== undefined ? Number(params.value).toFixed(2) : "";
    };

    return [
      {
        headerName: t("SI No."),
        valueGetter: (params) => {
          if (params.node.rowPinned === "bottom") return "";
          return params.node.rowIndex + 1;
        },
        width: 80,
        minWidth: 60,
        flex: 0,
        initialFlex: 0,
        headerClass: "ag-center-aligned-header",
        cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
        suppressMenu: true,
        sortable: false
      },
      {
        headerName: t("Description"),
        field: "description",
        headerClass: "ag-center-aligned-header",
        cellStyle: leftAlignColDef.cellStyle,
        minWidth: 180,
        flex: 1,
      },
      {
        headerName: t("Mass (kg)"),
        field: "mass",
        headerClass: "ag-center-aligned-header",
        cellStyle: rightAlignColDef.cellStyle,
        width: 110,
        minWidth: 80,
        valueFormatter: (params) => {
          // Building only: _massIsNA (set in BuildingCircularity.js's WBLCA merge) reflects
          // whether the source actually had a mass value — params.value itself is never
          // null since calculate_mass always fills in a computed number.
          if (onSubcategoryChange && params.data?._massIsNA) return t("N/A");
          return params.value !== null && params.value !== undefined
            ? Number(params.value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            : "";
        }
      },
      {
        headerName: t("MCI"),
        field: "mciVal",
        headerClass: "ag-center-aligned-header",
        cellStyle: rightAlignColDef.cellStyle,
        width: 80,
        minWidth: 60,
        valueFormatter: numericFormatter
      },
      {
        headerName: t("Connection Type (CT)"),
        field: "ct",
        headerClass: "ag-center-aligned-header",
        cellStyle: rightAlignColDef.cellStyle,
        width: 160,
        minWidth: 100,
        editable: isCircularityRowEditable,
        cellEditor: M3GridSelectEditor,
        cellEditorParams: { values: toEditorOptions(CT_OPTIONS) },
        valueFormatter: (params) => params.value !== null && params.value !== undefined ? Number(params.value).toFixed(1) : ""
      },
      {
        headerName: t("Connection Accessibility (CA)"),
        field: "ca",
        headerClass: "ag-center-aligned-header",
        cellStyle: rightAlignColDef.cellStyle,
        width: 200,
        minWidth: 120,
        editable: isCircularityRowEditable,
        cellEditor: M3GridSelectEditor,
        cellEditorParams: { values: toEditorOptions(CA_OPTIONS) },
        valueFormatter: (params) => params.value !== null && params.value !== undefined ? Number(params.value).toFixed(1) : ""
      },
      {
        headerName: t("Independency (ID)"),
        field: "indep",
        headerClass: "ag-center-aligned-header",
        cellStyle: rightAlignColDef.cellStyle,
        width: 140,
        minWidth: 100,
        editable: isCircularityRowEditable,
        cellEditor: M3GridSelectEditor,
        cellEditorParams: { values: toEditorOptions(INDEP_OPTIONS) },
        valueFormatter: (params) => params.value !== null && params.value !== undefined ? Number(params.value).toFixed(1) : ""
      },
      {
        headerName: t("Geometry of Product Edge (GPE)"),
        field: "gpe",
        headerClass: "ag-center-aligned-header",
        cellStyle: rightAlignColDef.cellStyle,
        width: 210,
        minWidth: 130,
        editable: isCircularityRowEditable,
        cellEditor: M3GridSelectEditor,
        cellEditorParams: { values: toEditorOptions(GPE_OPTIONS) },
        valueFormatter: (params) => params.value !== null && params.value !== undefined ? Number(params.value).toFixed(1) : ""
      },
      {
        headerName: t(onSubcategoryChange ? "Disassembly Factor (Fi)" : "Fi"),
        field: "fi",
        headerClass: "ag-center-aligned-header",
        cellStyle: rightAlignColDef.cellStyle,
        width: 70,
        minWidth: 50,
        valueFormatter: numericFormatter
      }
    ].map((col) => ({ ...defaultColDef, ...col, wrapHeaderText: false, autoHeaderHeight: false, resizable: true }));
  }, [t, onSubcategoryChange]);


  const gridRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      gridRef.current?.api?.autoSizeAllColumns();
    }, 150);
    return () => clearTimeout(timer);
  }, [computedRows]);

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 320px" }, gap: tok.spacing.sectionGap, alignItems: "start" }}>
      <DataTableCard
        title={t(onSubcategoryChange ? "PCI Calculation" : "PCI Calculation — Building / Connection Circularity Index")}
        actions={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {onSubcategoryChange && (
              <BuildingProductDropdown value={selectedSubcategoryId} onChange={onSubcategoryChange} showAllProductsOption={false} />
            )}
            <M3Button size="sm" variant="text" icon={<RestartAltOutlinedIcon sx={{ fontSize: 18 }} />} onClick={handleResetAll}>
              {t("Reset factors")}
            </M3Button>
          </Box>
        }
      >
      <Box sx={{ height: "auto" }}>
        <AgGrid
          ref={gridRef}
          columnDefs={colDefs}
          rowData={computedRows}
          headerCheckbox={false}
          suppressPagination={false}
          pagination={true}
          paginationPageSize={10}
          onCellValueChanged={onCellValueChanged}
          singleClickEdit={true}
          footerLeftContent={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>
                {t(type === "Project"
                  ? "Project Circularity Indicator"
                  : (onSubcategoryChange ? "Product Circularity Indicator(PCI)" : "Product Circularity Indicator"))}:
              </Typography>
              <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "text.primary" }}>
                {(metrics.circularityIndicator * 100).toFixed(2)}%
              </Typography>
            </Box>
          }
        />
      </Box>
      </DataTableCard>

      {/* Reference values for each disassembly factor */}
      <M3Surface padded style={{ maxHeight: 820, overflowY: "auto" }}>
        <Typography sx={{ ...tok.tableTitle, fontSize: tok.font.size.sectionTitle, fontWeight: tok.font.weight.semibold, textAlign: "left", pb: 1.5, mb: 2, borderBottom: `1px solid ${tok.border.divider}` }}>
          {t("Circularity Factors Reference")}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {FACTOR_REFERENCES.map((factor) => (
            <Box key={factor.title}>
              <Typography sx={{ fontFamily: tok.font.family, fontSize: tok.font.size.bodyText, fontWeight: tok.font.weight.semibold, color: tok.text.primary, textAlign: "left", mb: 1.5 }}>
                {t(factor.title)}
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                {factor.options.map((opt) => (
                  <Box key={opt.value} sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                    <Chip size="sm" tone={factor.tone}>{opt.value.toFixed(1)}</Chip>
                    <Typography sx={{ fontFamily: tok.font.family, fontSize: tok.font.size.caption, color: tok.text.secondary, textAlign: "left", lineHeight: 1.5 }}>
                      {t(opt.label)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </M3Surface>
    </Box>
  );
};

export default DesignAssessmentPanel;
